/**
 * Profile Service for Card Reader Profile Management
 * Handles CRUD operations and Firebase integration
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import {
  DetectionProfile,
  ProfileIndex,
  ProfileCategory,
  ProfileExportData,
  PROFILE_COLLECTIONS,
  isValidDetectionProfile,
  calculateProfileEffectiveness,
  ProfileStats,
  PerformanceMetrics,
  TrainingResults,
  DetectionThresholds,
  ReferenceCurve,
  normalizeSignatureData,
} from '../types/cardReaderProfiles';

/**
 * Mobile + web store thresholds differently:
 * - **Mobile (ProfileMode)**: `thresholds.avgThreshold`, `minThreshold`, `maxThreshold`, `useOr` (false = AND / all exceed), `useAvg`/`useMin`/`useMax`
 * - **Web legacy createProfile**: `thresholds.avgThreshold` = primary×10 only + `combinationLogic` (no min/max) — values often > 1
 * - **config.thresholds**: web FlowMatch primary/secondary shape
 */
function parseThresholdsFromFirestore(data: Record<string, any>): DetectionThresholds {
  const defaults: DetectionThresholds = {
    primaryThreshold: 0.5,
    secondaryThreshold: 0.3,
    combinationLogic: 'OR',
    confidence: 0.8,
    optimized: false,
  };

  const top = data?.thresholds;
  const cfg = data?.config?.thresholds;

  // 1) Mobile native: avg + min + max (see mobile-app/src/types/ProfileMode.ts)
  if (top && typeof top === 'object' && typeof top.minThreshold === 'number') {
    const avg = typeof top.avgThreshold === 'number' ? top.avgThreshold : defaults.primaryThreshold;
    const min = top.minThreshold;
    const max = typeof top.maxThreshold === 'number' ? top.maxThreshold : 0;
    const useOr = top.useOr === true;
    return {
      primaryThreshold: avg,
      secondaryThreshold: min,
      combinationLogic: useOr ? 'OR' : 'AND',
      confidence: typeof top.confidence === 'number' ? top.confidence : defaults.confidence,
      optimized: Boolean(top.optimized),
      trainingAccuracy:
        typeof top.trainingAccuracy === 'number' ? top.trainingAccuracy : undefined,
      averageThreshold: avg,
      minimumThreshold: min,
      maximumThreshold: max,
      useAvg: top.useAvg !== false,
      useMin: top.useMin !== false,
      useMax: top.useMax === true,
      useOr,
    };
  }

  // 2) Web FlowMatch `config.thresholds` (primary / secondary)
  if (
    cfg &&
    typeof cfg === 'object' &&
    (typeof cfg.primaryThreshold === 'number' ||
      typeof cfg.secondaryThreshold === 'number' ||
      typeof cfg.confidence === 'number')
  ) {
    const primary =
      typeof cfg.primaryThreshold === 'number' ? cfg.primaryThreshold : defaults.primaryThreshold;
    const secondary =
      typeof cfg.secondaryThreshold === 'number' ? cfg.secondaryThreshold : defaults.secondaryThreshold;
    const logicStr = String(cfg.combinationLogic || 'OR').toUpperCase();
    return {
      primaryThreshold: primary,
      secondaryThreshold: secondary,
      combinationLogic: logicStr === 'AND' ? 'AND' : 'OR',
      confidence: typeof cfg.confidence === 'number' ? cfg.confidence : defaults.confidence,
      optimized: Boolean(cfg.optimized),
      trainingAccuracy:
        typeof cfg.trainingAccuracy === 'number' ? cfg.trainingAccuracy : undefined,
    };
  }

  if (!top || typeof top !== 'object') {
    return defaults;
  }

  // 3) Legacy web-only doc: { avgThreshold, combinationLogic } where avgThreshold was primary×10 (often > 1)
  if (typeof top.avgThreshold === 'number' && typeof top.primaryThreshold !== 'number') {
    const raw = top.avgThreshold;
    const primary = raw > 1 ? raw / 10 : raw;
    let secondary = defaults.secondaryThreshold;
    if (typeof top.secondaryThreshold === 'number') {
      secondary = top.secondaryThreshold > 1 ? top.secondaryThreshold / 10 : top.secondaryThreshold;
    } else if (typeof top.secondaryAvgThreshold === 'number') {
      const r = top.secondaryAvgThreshold;
      secondary = r > 1 ? r / 10 : r;
    }
    return {
      primaryThreshold: primary,
      secondaryThreshold: secondary,
      combinationLogic: String(top.combinationLogic || 'OR').toUpperCase() === 'AND' ? 'AND' : 'OR',
      confidence: typeof top.confidence === 'number' ? top.confidence : defaults.confidence,
      optimized: Boolean(top.optimized),
      trainingAccuracy:
        typeof top.trainingAccuracy === 'number' ? top.trainingAccuracy : undefined,
    };
  }

  if (typeof top.primaryThreshold === 'number') {
    return {
      primaryThreshold: top.primaryThreshold,
      secondaryThreshold:
        typeof top.secondaryThreshold === 'number' ? top.secondaryThreshold : defaults.secondaryThreshold,
      combinationLogic: String(top.combinationLogic || 'OR').toUpperCase() === 'AND' ? 'AND' : 'OR',
      confidence: typeof top.confidence === 'number' ? top.confidence : defaults.confidence,
      optimized: Boolean(top.optimized),
      trainingAccuracy:
        typeof top.trainingAccuracy === 'number' ? top.trainingAccuracy : undefined,
    };
  }

  return defaults;
}

/** Mobile uses cleanSD / skimmerSD; web ProfileStats uses cleanDetections / skimmerDetections / totalScansProcessed */
function parseStatsFromFirestore(data: Record<string, any>): ProfileStats {
  const s = data.stats || {};
  const clean = Number(s.cleanDetections ?? s.cleanSD ?? 0) || 0;
  const skimmer = Number(s.skimmerDetections ?? s.skimmerSD ?? 0) || 0;
  const summed = clean + skimmer;
  const total =
    typeof s.totalScansProcessed === 'number' && s.totalScansProcessed > 0
      ? s.totalScansProcessed
      : summed > 0
        ? summed
        : typeof s.totalScans === 'number'
          ? s.totalScans
          : 0;

  const lastRaw = s.lastUsed ?? data.lastSyncedAt ?? data.createdAt;
  let lastUsed: Date | Timestamp = Timestamp.now();
  if (lastRaw instanceof Timestamp) {
    lastUsed = lastRaw;
  } else if (lastRaw instanceof Date) {
    lastUsed = lastRaw;
  } else if (lastRaw != null && typeof (lastRaw as any).toDate === 'function') {
    lastUsed = Timestamp.fromDate((lastRaw as any).toDate());
  } else if (lastRaw != null && typeof (lastRaw as any).seconds === 'number') {
    lastUsed = new Timestamp((lastRaw as any).seconds, (lastRaw as any).nanoseconds || 0);
  } else if (lastRaw != null) {
    const d = new Date(lastRaw as string | number);
    lastUsed = isNaN(d.getTime()) ? Timestamp.now() : Timestamp.fromDate(d);
  }

  return {
    totalScansProcessed: total,
    cleanDetections: clean,
    skimmerDetections: skimmer,
    averageConfidence: Number(s.averageConfidence ?? s.avgConfidence ?? 0) || 0,
    lastUsed,
    usageCount: Number(s.usageCount ?? 0) || 0,
    falsePositiveRate: Number(s.falsePositiveRate ?? 0) || 0,
    falseNegativeRate: Number(s.falseNegativeRate ?? 0) || 0,
    effectivenessScore: Number(s.effectivenessScore ?? 75) || 75,
  };
}

function mapCurvesFromFirestore(data: Record<string, any>): ReferenceCurve[] {
  const fromRefRaw = Array.isArray(data.referenceCurves) ? data.referenceCurves : [];
  const fromFlatRaw = Array.isArray(data.curves) ? data.curves : [];

  const mapFlat = (curves: any[]): ReferenceCurve[] =>
    curves.map((curveObj: any, index: number) => {
      const rawSig =
        curveObj?.points ?? curveObj?.signature ?? (Array.isArray(curveObj) ? curveObj : undefined);
      return {
        id: typeof curveObj?.id === 'string' && curveObj.id.length ? curveObj.id : `curve_${index}`,
        signature: normalizeSignatureData(rawSig),
        originalScanId: typeof curveObj?.originalScanId === 'string' ? curveObj.originalScanId : `scan_${index}`,
        diversity: Number(curveObj?.diversity ?? 1) || 1,
        weight: Number(curveObj?.weight ?? 1) || 1,
      };
    });

  const normalizeRef = (curves: any[]): ReferenceCurve[] =>
    curves.map((c: any, index: number) => {
      const rawSig = c?.signature ?? c?.points;
      return {
        id: typeof c?.id === 'string' && c.id.length ? c.id : `curve_${index}`,
        signature: normalizeSignatureData(rawSig),
        originalScanId: typeof c?.originalScanId === 'string' ? c.originalScanId : `scan_${index}`,
        diversity: Number(c?.diversity ?? 1) || 1,
        weight: Number(c?.weight ?? 1) || 1,
      };
    });

  const fromRef = normalizeRef(fromRefRaw);
  const fromFlat = mapFlat(fromFlatRaw);

  // Mobile sync stores flattened `curves`; web may also store rich `referenceCurves`.
  // Never let a shorter `curves` array wipe a full `referenceCurves` list.
  if (fromRef.length > 0 && fromRef.length >= fromFlat.length) {
    return fromRef;
  }
  if (fromFlat.length > 0) {
    return fromFlat;
  }
  return fromRef;
}

/**
 * Mobile / legacy docs may store the reader thumbnail under different keys.
 * Prefer HTTPS download URLs from Firebase Storage.
 */
function resolveReaderImageUrlFromFirestore(data: Record<string, any>): string | undefined {
  const candidates = [
    data.readerImageUrl,
    data.reader_image_url,
    data.readerImageURL,
    data.ReaderImageUrl,
    data.photoUrl,
    data.readerPhotoUrl,
    data.thumbnailUrl,
    data.trainingInfo?.readerImageUrl,
    data.metadata?.readerImageUrl,
  ];
  for (const c of candidates) {
    if (typeof c !== 'string') continue;
    const t = c.trim();
    if (!t) continue;
    if (/^https?:\/\//i.test(t)) return t;
  }
  return undefined;
}

/** Single place to map `detectionProfiles` Firestore doc → web `DetectionProfile` */
export function mapFirestoreDocToDetectionProfile(docId: string, data: Record<string, any>): DetectionProfile {
  let validCategory: ProfileCategory = 'dip';
  if (data.category && ['dip', 'swipe'].includes(data.category)) {
    validCategory = data.category as ProfileCategory;
  } else if (data.category) {
    console.warn(`Invalid or missing category '${data.category}' for profile ${docId}, defaulting to 'dip'`);
  }

  const referenceCurves = mapCurvesFromFirestore(data);
  const thresholds = parseThresholdsFromFirestore(data);
  const stats = parseStatsFromFirestore(data);
  const readerImageUrl = resolveReaderImageUrlFromFirestore(data);

  return {
    ...data,
    id: docId,
    category: validCategory,
    referenceCurves,
    readerImageUrl: readerImageUrl ?? data.readerImageUrl,
    stats,
    thresholds,
  } as DetectionProfile;
}

export class ProfileService {
  /**
   * Create a new detection profile
   * Uses same structure as mobile app: detectionProfiles collection
   */
  static async createProfile(
    profile: Omit<DetectionProfile, 'id' | 'createdAt' | 'updatedAt'>,
    masterOwnerID: string
  ): Promise<DetectionProfile> {
    const profileId = `profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const now = Timestamp.now();
    
    const newProfile: DetectionProfile = {
      ...profile,
      id: profileId,
      masterOwnerID,
      createdAt: now,
      updatedAt: now,
      stats: {
        ...profile.stats,
        totalScansProcessed: profile.stats?.totalScansProcessed || 0,
        cleanDetections: profile.stats?.cleanDetections || 0,
        skimmerDetections: profile.stats?.skimmerDetections || 0,
        averageConfidence: profile.stats?.averageConfidence || 0,
        lastUsed: profile.stats?.lastUsed || now,
        usageCount: profile.stats?.usageCount || 0,
        falsePositiveRate: profile.stats?.falsePositiveRate || 0,
        falseNegativeRate: profile.stats?.falseNegativeRate || 0,
        effectivenessScore: profile.stats?.effectivenessScore || 0
      }
    };

    // Validate profile data
    if (!isValidDetectionProfile(newProfile)) {
      throw new Error('Invalid profile data structure');
    }

    try {
      // Use same collection as mobile app: detectionProfiles
      const PROFILES_COLLECTION = 'detectionProfiles';
      const profileRef = doc(db, PROFILES_COLLECTION, profileId);
      
      // Get current Firebase Auth user for mobile compatibility
      const currentFirebaseUser = auth.currentUser;
      
      // Prepare data in EXACT mobile app format
      const profileForFirebase = {
        // Core mobile app fields in exact same structure
        category: newProfile.category,
        companyId: masterOwnerID,
        config: {
          activeCropThreshold: 0.1,
          thresholds: newProfile.thresholds || {
            primaryThreshold: 0.5,
            secondaryThreshold: 0.3,
            combinationLogic: 'OR',
            confidence: 0.8,
            optimized: false
          }
        },
        createdAt: newProfile.createdAt instanceof Timestamp ? newProfile.createdAt.toDate().toISOString() : newProfile.createdAt.toISOString(), // ISO string format like mobile
        createdBy: currentFirebaseUser?.email || 'web-app@skimbuster.com',
        curves: newProfile.referenceCurves.map((curve, index) => ({
          index,
          points: curve.signature
        })),
        deviceId: '20:6E:F1:B9:9B:45', // Default device ID
        deviceName: 'SkimBusterV1',
        firebaseDocId: profileId,
        id: profileId,
        isPublic: false,
        isShared: false,
        knownSkimmers: {
          deepInsert: [],
          overlay: []
        },
        lastSyncedAt: newProfile.updatedAt instanceof Timestamp ? newProfile.updatedAt.toDate().toISOString() : newProfile.updatedAt.toISOString(),
        name: newProfile.name,
        readerImageUrl: `https://firebasestorage.googleapis.com/v0/b/skimbuster-micclo.firebasestorage.app/o/reader_images%2F${profileId}.jpg?alt=media&token=generated-token`,
        sizeBytes: 21702,
        stats: {
          avgConfidence: newProfile.stats?.averageConfidence || 0,
          cleanSD: newProfile.stats?.cleanDetections || 0,
          skimmerSD: newProfile.stats?.skimmerDetections || 0
        },
        syncStatus: 'synced',
        thresholds: {
          avgThreshold: (newProfile.thresholds?.primaryThreshold || 0.5) * 10, // Convert to mobile format
          combinationLogic: newProfile.thresholds?.combinationLogic || 'OR'
        },
        trainingInfo: {
          location: 'WEB',
          readerModel: newProfile.description || `FlowMatch-based detection profile created from ${newProfile.referenceCurves?.length || 0} training scans`,
          notes: 'Created via web app'
        },
        trainingScans: {
          duration: 1429, // Default duration
          sampleCount: newProfile.referenceCurves?.reduce((sum, curve) => sum + curve.signature.length, 0) || 0
        },
        updatedAt: {
          nanoseconds: 518000000,
          seconds: Math.floor((newProfile.updatedAt instanceof Timestamp ? newProfile.updatedAt.toDate().getTime() : newProfile.updatedAt.getTime()) / 1000)
        },
        userId: masterOwnerID,
        version: 1
      };
      
      await setDoc(profileRef, profileForFirebase);
      
      console.log('✅ Created profile in collection:', `${PROFILES_COLLECTION}/${profileId}`);
      console.log('📋 Profile data saved in mobile app format:', {
        id: profileForFirebase.id,
        name: profileForFirebase.name,
        category: profileForFirebase.category,
        companyId: profileForFirebase.companyId,
        userId: profileForFirebase.userId,
        deviceName: profileForFirebase.deviceName,
        curveCount: profileForFirebase.curves?.length || 0,
        syncStatus: profileForFirebase.syncStatus,
        version: profileForFirebase.version
      });
      
      // Update profile index
      await this.updateProfileIndex(masterOwnerID, 'add', newProfile);
      
      return newProfile;
    } catch (error) {
      console.error('Error creating profile:', error);
      throw new Error('Failed to create detection profile');
    }
  }

  /**
   * Get all profiles for a company
   * Uses the same structure as mobile app: detectionProfiles collection with companyId/userId filtering
   */
  static async getProfiles(masterOwnerID: string): Promise<DetectionProfile[]> {
    try {
      console.log('🔍 ProfileService.getProfiles() called');
      console.log('📍 Master Owner ID:', masterOwnerID);
      
      // Use the same collection name as mobile app
      const PROFILES_COLLECTION = 'detectionProfiles';
      const profilesCollection = collection(db, PROFILES_COLLECTION);
      
      console.log(`📂 Querying collection: ${PROFILES_COLLECTION}`);
      
      // First try querying by companyId (like mobile app)
      console.log('🔍 Trying query by companyId...');
      let snapshot;
      try {
        const companyQuery = query(profilesCollection, where('companyId', '==', masterOwnerID));
        snapshot = await getDocs(companyQuery);
        console.log(`📊 Company query result: ${snapshot.size} documents found`);
      } catch (error: any) {
        console.log(`⚠️ Company query failed:`, error.message);
      }
      
      // If no results with companyId, try masterOwnerID field
      if (!snapshot || snapshot.empty) {
        console.log('🔍 Trying query by masterOwnerID...');
        try {
          const masterQuery = query(profilesCollection, where('masterOwnerID', '==', masterOwnerID));
          snapshot = await getDocs(masterQuery);
          console.log(`📊 Master query result: ${snapshot.size} documents found`);
        } catch (error: any) {
          console.log(`⚠️ Master query failed:`, error.message);
        }
      }
      
      // If still no results, try userId field
      if (!snapshot || snapshot.empty) {
        console.log('🔍 Trying query by userId...');
        try {
          const userQuery = query(profilesCollection, where('userId', '==', masterOwnerID));
          snapshot = await getDocs(userQuery);
          console.log(`📊 User query result: ${snapshot.size} documents found`);
        } catch (error: any) {
          console.log(`⚠️ User query failed:`, error.message);
        }
      }
      
      // If still no results, try with current Firebase Auth user ID
      if (!snapshot || snapshot.empty) {
        const currentFirebaseUser = auth.currentUser;
        if (currentFirebaseUser) {
          console.log('🔍 Trying query by Firebase Auth UID...');
          try {
            const firebaseQuery = query(profilesCollection, where('userId', '==', currentFirebaseUser.uid));
            snapshot = await getDocs(firebaseQuery);
            console.log(`📊 Firebase Auth query result: ${snapshot.size} documents found`);
          } catch (error: any) {
            console.log(`⚠️ Firebase Auth query failed:`, error.message);
          }
        }
      }
      
      if (!snapshot || snapshot.empty) {
        console.log('ℹ️ No profiles found for any query method');
        return [];
      }
      
      const profiles: DetectionProfile[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        
        console.log(`📄 Profile found:`, {
          id: doc.id,
          name: data.name || 'No name',
          category: data.category || 'No category',
          companyId: data.companyId || 'not set',
          userId: data.userId || 'not set',
          masterOwnerID: data.masterOwnerID || 'not set',
          dataKeys: Object.keys(data)
        });

        profiles.push(mapFirestoreDocToDetectionProfile(doc.id, data));
      });
      
      console.log(`✅ Successfully loaded ${profiles.length} profiles from ${PROFILES_COLLECTION}`);
      return profiles;
      
    } catch (error: any) {
      console.error('❌ Error in ProfileService.getProfiles():', {
        message: error.message,
        code: error.code,
        details: error.details,
        stack: error.stack
      });
      
      throw new Error(`Failed to fetch detection profiles: ${error.message}`);
    }
  }

  /**
   * Get a specific profile by ID
   */
  static async getProfile(profileId: string, masterOwnerID?: string): Promise<DetectionProfile | null> {
    try {
      // If masterOwnerID is not provided, we need to search across all users (not ideal)
      // For now, require masterOwnerID
      if (!masterOwnerID) {
        console.warn('masterOwnerID is required for getProfile');
        return null;
      }
      
      const profilesPath = `profiles/${masterOwnerID}`;
      const profileRef = doc(db, profilesPath, profileId);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        const data = { ...profileSnap.data(), id: profileSnap.id };
        return data as DetectionProfile;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching profile:', error);
      throw new Error('Failed to fetch detection profile');
    }
  }

  /**
   * Update an existing profile
   */
  static async updateProfile(
    profileId: string,
    masterOwnerID: string,
    updates: Partial<DetectionProfile>
  ): Promise<void> {
    try {
      // Use the same collection as create/get methods
      const PROFILES_COLLECTION = 'detectionProfiles';
      const profileRef = doc(db, PROFILES_COLLECTION, profileId);
      
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now()
      };
      
      await updateDoc(profileRef, updateData);
      
      // If we're updating stats, recalculate effectiveness score
      if (updates.stats) {
        const effectivenessScore = calculateProfileEffectiveness(updates.stats);
        await updateDoc(profileRef, { 
          'stats.effectivenessScore': effectivenessScore 
        });
      }
      
    } catch (error) {
      console.error('Error updating profile:', error);
      throw new Error('Failed to update detection profile');
    }
  }

  /**
   * Delete a profile
   */
  static async deleteProfile(profileId: string, masterOwnerID: string): Promise<void> {
    try {
      // Use the same collection as create/get methods
      const PROFILES_COLLECTION = 'detectionProfiles';
      const profileRef = doc(db, PROFILES_COLLECTION, profileId);
      
      // Get profile before deleting for verification
      const profileSnap = await getDoc(profileRef);
      if (!profileSnap.exists()) {
        throw new Error('Profile not found');
      }
      
      const profileData = profileSnap.data();
      
      // Verify ownership before deletion
      if (profileData.masterOwnerID !== masterOwnerID && 
          profileData.companyId !== masterOwnerID && 
          profileData.userId !== masterOwnerID) {
        throw new Error('Unauthorized: Cannot delete profile owned by another user');
      }
      
      // Actually delete the profile document (hard delete)
      await deleteDoc(profileRef);
      
      console.log(`✅ Profile deleted: ${profileId}`);
      
    } catch (error) {
      console.error('Error deleting profile:', error);
      throw new Error(`Failed to delete detection profile: ${(error as Error).message}`);
    }
  }

  /**
   * Get profiles by category
   */
  static async getProfilesByCategory(
    masterOwnerID: string,
    category: ProfileCategory
  ): Promise<DetectionProfile[]> {
    try {
      const profilesPath = `profiles/${masterOwnerID}`;
      const profilesQuery = query(
        collection(db, profilesPath),
        where('category', '==', category)
      );
      
      const snapshot = await getDocs(profilesQuery);
      const profiles: DetectionProfile[] = [];
      
      snapshot.forEach(doc => {
        const data = { ...doc.data(), id: doc.id };
        profiles.push(data as DetectionProfile);
      });
      
      // Sort by effectiveness score
      profiles.sort((a, b) => (b.stats?.effectivenessScore || 0) - (a.stats?.effectivenessScore || 0));
      
      return profiles;
    } catch (error) {
      console.error('Error fetching profiles by category:', error);
      throw new Error('Failed to fetch profiles by category');
    }
  }

  /**
   * Activate a profile for its category (deactivates others in same category)
   */
  static async activateProfile(profileId: string, masterOwnerID: string): Promise<void> {
    try {
      const profile = await this.getProfile(profileId, masterOwnerID);
      if (!profile) {
        throw new Error('Profile not found');
      }

      const profilesPath = `profiles/${masterOwnerID}`;
      const batch = writeBatch(db);
      
      // Deactivate all profiles in the same category
      const categoryProfiles = await this.getProfilesByCategory(masterOwnerID, profile.category);
      
      categoryProfiles.forEach(p => {
        if (p.id !== profileId) {
          const ref = doc(db, profilesPath, p.id);
          batch.update(ref, { isActive: false });
        }
      });
      
      // Activate the selected profile
      const profileRef = doc(db, profilesPath, profileId);
      batch.update(profileRef, { 
        isActive: true,
        updatedAt: Timestamp.now()
      });
      
      await batch.commit();
      
      // Update profile index
      await this.updateActiveProfileInIndex(masterOwnerID, profile.category, profileId);
      
    } catch (error) {
      console.error('Error activating profile:', error);
      throw new Error('Failed to activate profile');
    }
  }

  /**
   * Deactivate a profile
   */
  static async deactivateProfile(profileId: string, masterOwnerID: string): Promise<void> {
    try {
      const profile = await this.getProfile(profileId, masterOwnerID);
      if (!profile) {
        throw new Error('Profile not found');
      }

      const profilesPath = `profiles/${masterOwnerID}`;
      const profileRef = doc(db, profilesPath, profileId);
      await updateDoc(profileRef, { 
        isActive: false,
        updatedAt: Timestamp.now()
      });
      
      // Update profile index
      await this.updateActiveProfileInIndex(masterOwnerID, profile.category, null);
      
    } catch (error) {
      console.error('Error deactivating profile:', error);
      throw new Error('Failed to deactivate profile');
    }
  }

  /**
   * Get the active profile for a category
   */
  static async getActiveProfile(
    masterOwnerID: string,
    category: ProfileCategory
  ): Promise<DetectionProfile | null> {
    try {
      const profilesPath = `profiles/${masterOwnerID}`;
      const profilesQuery = query(
        collection(db, profilesPath),
        where('category', '==', category),
        where('isActive', '==', true),
        limit(1)
      );
      
      const snapshot = await getDocs(profilesQuery);
      
      if (snapshot.empty) {
        return null;
      }
      
      const doc = snapshot.docs[0];
      const data = { ...doc.data(), id: doc.id };
      
      return data as DetectionProfile;
    } catch (error) {
      console.error('Error fetching active profile:', error);
      return null;
    }
  }

  /**
   * Update profile usage statistics
   */
  static async updateProfileStats(
    profileId: string,
    masterOwnerID: string,
    scanResult: 'clean' | 'skimmer',
    confidence: number
  ): Promise<void> {
    try {
      const profile = await this.getProfile(profileId, masterOwnerID);
      if (!profile) {
        throw new Error('Profile not found');
      }

      const stats = profile.stats;
      const newStats: ProfileStats = {
        ...stats,
        totalScansProcessed: stats.totalScansProcessed + 1,
        cleanDetections: scanResult === 'clean' ? stats.cleanDetections + 1 : stats.cleanDetections,
        skimmerDetections: scanResult === 'skimmer' ? stats.skimmerDetections + 1 : stats.skimmerDetections,
        averageConfidence: ((stats.averageConfidence * stats.totalScansProcessed) + confidence) / (stats.totalScansProcessed + 1),
        lastUsed: Timestamp.now(),
        usageCount: stats.usageCount + 1,
        effectivenessScore: 0 // Will be recalculated
      };

      // Recalculate effectiveness score
      newStats.effectivenessScore = calculateProfileEffectiveness(newStats);

      await this.updateProfile(profileId, masterOwnerID, { stats: newStats });
      
    } catch (error) {
      console.error('Error updating profile stats:', error);
      throw new Error('Failed to update profile statistics');
    }
  }

  /**
   * Create or update profile index for efficient listing
   */
  private static async updateProfileIndex(
    masterOwnerID: string,
    action: 'add' | 'remove' | 'update',
    profile: DetectionProfile
  ): Promise<void> {
    try {
      const indexRef = doc(db, PROFILE_COLLECTIONS.PROFILE_INDEXES, masterOwnerID);
      const indexSnap = await getDoc(indexRef);
      
      let profileIndex: ProfileIndex;
      
      if (indexSnap.exists()) {
        profileIndex = indexSnap.data() as ProfileIndex;
      } else {
        profileIndex = {
          profiles: [],
          activeProfilesByCategory: {
            dip: null,
            swipe: null
          },
          lastSync: Timestamp.now(),
          totalProfiles: 0
        };
      }

      const profileSummary = {
        id: profile.id,
        name: profile.name,
        category: profile.category,
        isActive: profile.isActive,
        lastUsed: profile.stats.lastUsed,
        effectivenessScore: profile.stats.effectivenessScore
      };

      switch (action) {
        case 'add':
          profileIndex.profiles.push(profileSummary);
          profileIndex.totalProfiles++;
          if (profile.isActive) {
            profileIndex.activeProfilesByCategory[profile.category] = profile.id;
          }
          break;
          
        case 'remove':
          profileIndex.profiles = profileIndex.profiles.filter(p => p.id !== profile.id);
          profileIndex.totalProfiles--;
          if (profileIndex.activeProfilesByCategory[profile.category] === profile.id) {
            profileIndex.activeProfilesByCategory[profile.category] = null;
          }
          break;
          
        case 'update':
          const index = profileIndex.profiles.findIndex(p => p.id === profile.id);
          if (index !== -1) {
            profileIndex.profiles[index] = profileSummary;
          }
          if (profile.isActive) {
            profileIndex.activeProfilesByCategory[profile.category] = profile.id;
          }
          break;
      }

      profileIndex.lastSync = Timestamp.now();
      await setDoc(indexRef, profileIndex);
      
    } catch (error) {
      console.error('Error updating profile index:', error);
      // Don't throw error as this is not critical
    }
  }

  /**
   * Update active profile in index
   */
  private static async updateActiveProfileInIndex(
    masterOwnerID: string,
    category: ProfileCategory,
    profileId: string | null
  ): Promise<void> {
    try {
      const indexRef = doc(db, PROFILE_COLLECTIONS.PROFILE_INDEXES, masterOwnerID);
      await updateDoc(indexRef, {
        [`activeProfilesByCategory.${category}`]: profileId,
        lastSync: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating active profile in index:', error);
      // Don't throw error as this is not critical
    }
  }

  /**
   * Get profile index for efficient listing
   */
  static async getProfileIndex(masterOwnerID: string): Promise<ProfileIndex | null> {
    try {
      const indexRef = doc(db, PROFILE_COLLECTIONS.PROFILE_INDEXES, masterOwnerID);
      const indexSnap = await getDoc(indexRef);
      
      if (indexSnap.exists()) {
        return indexSnap.data() as ProfileIndex;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching profile index:', error);
      return null;
    }
  }

  /**
   * Get all public profiles from Firebase
   */
  static async getPublicProfiles(): Promise<DetectionProfile[]> {
    try {
      console.log('🌐 ProfileService.getPublicProfiles() called');
      
      const PROFILES_COLLECTION = 'detectionProfiles';
      const profilesCollection = collection(db, PROFILES_COLLECTION);
      
      console.log('📂 Querying public profiles...');
      const publicQuery = query(profilesCollection, where('isPublic', '==', true));
      const snapshot = await getDocs(publicQuery);
      
      console.log(`📊 Public query result: ${snapshot.size} documents found`);
      
      if (snapshot.empty) {
        console.log('ℹ️ No public profiles found');
        return [];
      }
      
      const profiles: DetectionProfile[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        
        console.log(`📄 Public profile found:`, {
          id: doc.id,
          name: data.name || 'No name',
          category: data.category || 'No category',
          isPublic: data.isPublic,
          companyId: data.companyId || 'not set'
        });

        profiles.push(mapFirestoreDocToDetectionProfile(doc.id, data));
      });
      
      console.log(`✅ Successfully loaded ${profiles.length} public profiles`);
      return profiles;
      
    } catch (error: any) {
      console.error('❌ Error in ProfileService.getPublicProfiles():', error);
      throw new Error(`Failed to fetch public profiles: ${error.message}`);
    }
  }

  /**
   * Build a portable export payload for a profile
   */
  static buildExportData(profile: DetectionProfile, exportedBy: string): ProfileExportData {
    return {
      profile,
      exportMetadata: {
        exportedAt: Timestamp.now(),
        exportedBy: exportedBy || 'web-app',
        version: '1.0',
        format: 'json'
      }
    };
  }

  private static normalizeTimestamp(value: any): Date | Timestamp {
    if (!value) {
      return Timestamp.now();
    }
    if (value instanceof Timestamp) {
      return value;
    }
    if (typeof value?.seconds === 'number') {
      return new Timestamp(value.seconds, value.nanoseconds || 0);
    }
    if (typeof value === 'string' || typeof value === 'number') {
      return new Date(value);
    }
    return value;
  }

  /**
   * Import a profile from export data and create a new profile
   */
  static async importProfileFromData(
    rawData: unknown,
    masterOwnerID: string,
    importedBy?: string
  ): Promise<DetectionProfile> {
    const data = rawData as any;
    const profileData = data?.profile || data;

    if (!isValidDetectionProfile(profileData)) {
      throw new Error('Invalid profile export format');
    }

    const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, masterOwnerID: _owner, ...rest } = profileData;

    const normalizedProfile: Omit<DetectionProfile, 'id' | 'createdAt' | 'updatedAt'> = {
      ...rest,
      name: rest.name?.trim() || 'Imported Profile',
      category: rest.category === 'dip' || rest.category === 'swipe' ? rest.category : 'dip',
      referenceCurves: rest.referenceCurves || [],
      thresholds: rest.thresholds || {
        primaryThreshold: 0.5,
        secondaryThreshold: 0.3,
        combinationLogic: 'OR',
        confidence: 0.8,
        optimized: false
      },
      stats: {
        totalScansProcessed: rest.stats?.totalScansProcessed || 0,
        cleanDetections: rest.stats?.cleanDetections || 0,
        skimmerDetections: rest.stats?.skimmerDetections || 0,
        averageConfidence: rest.stats?.averageConfidence || 0,
        lastUsed: this.normalizeTimestamp(rest.stats?.lastUsed),
        usageCount: rest.stats?.usageCount || 0,
        falsePositiveRate: rest.stats?.falsePositiveRate || 0,
        falseNegativeRate: rest.stats?.falseNegativeRate || 0,
        effectivenessScore: rest.stats?.effectivenessScore || 75
      },
      processingParameters: rest.processingParameters || {
        signatureLength: 800,
        samplingRate: 1000,
        filterParameters: {},
        normalizationMethod: 'z-score',
        activeCroppingEnabled: true
      },
      trainingSessionId: rest.trainingSessionId || `session_${Date.now()}`,
      createdBy: importedBy || rest.createdBy || 'web-import',
      isActive: rest.isActive ?? false,
      isShared: rest.isShared ?? false,
      tags: rest.tags || [],
      version: rest.version || 1,
      masterOwnerID
    };

    return this.createProfile(normalizedProfile, masterOwnerID);
  }

  /**
   * Copy a public profile to the user's company
   */
  static async copyProfileToCompany(
    sourceProfileId: string, 
    masterOwnerID: string,
    newName?: string
  ): Promise<DetectionProfile> {
    try {
      console.log('🔄 Copying profile to company...', { sourceProfileId, masterOwnerID });
      
      // First, get the source profile
      const PROFILES_COLLECTION = 'detectionProfiles';
      const sourceRef = doc(db, PROFILES_COLLECTION, sourceProfileId);
      const sourceSnap = await getDoc(sourceRef);
      
      if (!sourceSnap.exists()) {
        throw new Error('Source profile not found');
      }
      
      const sourceData = sourceSnap.data() as Record<string, any>;
      
      // Create new profile ID
      const newProfileId = `profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const now = Timestamp.now();

      const normalized = mapFirestoreDocToDetectionProfile(sourceProfileId, sourceData);

      // Create copied profile with new ownership
      const copiedProfile: DetectionProfile = {
        ...normalized,
        id: newProfileId,
        name: newName || `${sourceData.name} (Copy)`,
        masterOwnerID,
        companyId: masterOwnerID,
        userId: masterOwnerID,
        isActive: false, // New copied profiles start as inactive
        isShared: false, // Make it private by default
        createdAt: now,
        updatedAt: now,
        // Reset stats for the copied profile (keep thresholds/curves from normalized source)
        stats: {
          totalScansProcessed: 0,
          cleanDetections: 0,
          skimmerDetections: 0,
          averageConfidence: 0,
          lastUsed: now,
          usageCount: 0,
          falsePositiveRate: 0,
          falseNegativeRate: 0,
          effectivenessScore: 75
        },
        thresholds: normalized.thresholds,
      } as DetectionProfile;
      
      // Save to Firebase
      const newProfileRef = doc(db, PROFILES_COLLECTION, newProfileId);
      await setDoc(newProfileRef, copiedProfile);
      
      console.log('✅ Profile copied successfully:', newProfileId);
      return copiedProfile;
      
    } catch (error: any) {
      console.error('❌ Error copying profile:', error);
      throw new Error(`Failed to copy profile: ${error.message}`);
    }
  }

  /**
   * Toggle profile visibility (public/private)
   * Only Admin users can change profile privacy settings
   */
  static async toggleProfileVisibility(
    profileId: string, 
    masterOwnerID: string,
    isPublic: boolean,
    userRole?: string
  ): Promise<void> {
    try {
      console.log('👁️ Toggling profile visibility...', { profileId, isPublic, userRole });
      
      // Validate user role - only Admin users can change profile privacy
      if (userRole !== 'Admin') {
        throw new Error('Only Admin users can change profile privacy settings');
      }
      
      const PROFILES_COLLECTION = 'detectionProfiles';
      const profileRef = doc(db, PROFILES_COLLECTION, profileId);
      
      // Verify the profile belongs to the user's company
      const profileDoc = await getDoc(profileRef);
      if (!profileDoc.exists()) {
        throw new Error('Profile not found');
      }
      
      const profileData = profileDoc.data();
      if (profileData.companyId !== masterOwnerID && profileData.userId !== masterOwnerID && profileData.masterOwnerID !== masterOwnerID) {
        throw new Error('Unauthorized: Profile does not belong to your company');
      }
      
      await updateDoc(profileRef, {
        isShared: isPublic, // For compatibility with mobile app
        isPublic: isPublic,
        updatedAt: Timestamp.now()
      });
      
      console.log(`✅ Profile visibility updated: ${isPublic ? 'public' : 'private'}`);
      
    } catch (error: any) {
      console.error('❌ Error toggling profile visibility:', error);
      throw new Error(`Failed to update profile visibility: ${error.message}`);
    }
  }

}

export default ProfileService;